import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { Link, useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextArea from "@/common/components/TextArea";
import useRewardItem from "../../hooks/useRewardItem";
import { ShowHeader } from "@/common/components/ShowHeader";
import Select from "@/common/components/Select";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { formatCurrency, formatDate } from "@/common/utils/formatters";

const Show = () => {
  const { id } = useParams();

  const {
    data: initialRewardItem,
    isPending: initialRewardItemIsPending,
    error: initialRewardItemError = {} as any,
    refetch: refetchRewardItem,
  } = useRewardItem(id || '');

  const disableUpdateButton = initialRewardItemIsPending || initialRewardItemError?.status === 403;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-reward-items` }}
        title="Itens para resgate"
        subtitle={initialRewardItem?.display_name}
        buttons={(
          <Link
            to={disableUpdateButton ? '' : `/tmais-reward-items/${id}/edit`}
            className={disableUpdateButton ? "cursor-default" : ''}
          >
            <Button operationKlassToCheckPermission="Admin::Tmais::Products::Update" data-testid='update_reward_item_button' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" disabled={disableUpdateButton}>
              Editar
            </Button>
          </Link>
        )}
      />

      {initialRewardItemIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={initialRewardItemError?.status}
          refetchFunction={refetchRewardItem}
        >
          <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <TextField
              label="Estabelecimento"
              value={initialRewardItem?.establishment_company_branch?.general_information?.fantasy_name}
              data-testid="establishment_display_name_input"
              className="w-full"
              disabled
            />

            <h2 className="text-lg font-bold">Detalhes da Recompensa</h2>

            <div className="flex gap-4">
              {initialRewardItem?.pictures?.length ? (
                <ImageCarousel 
                  files={initialRewardItem.pictures.map((photo, i) => ({ id: String(i), file: photo.url }))} 
                  showRemoveButton={false} 
                />
              ) : null}
              <div className="flex flex-col gap-4 w-full">
                <div className="flex items-center gap-2">
                  <TextField
                    label="Nome"
                    value={initialRewardItem?.display_name}
                    data-testid="display_name_input"
                    className="w-1/2"
                    disabled
                  />
                  <TextField
                    label="SKU/Código do produto"
                    value={initialRewardItem?.sku}
                    data-testid="sku_input"
                    className="w-1/2"
                    disabled
                  />
                </div>

                <TextArea
                  label="Descrição"
                  value={initialRewardItem?.description}
                  data-testid="description_input"
                  rows={8}
                  disabled
                />

                <Select
                  isDisabled
                  isMulti
                  label="Destacar item com"
                  data-testid="product_badges_input"
                  value={initialRewardItem?.product_badges?.map((productBadge) => ({
                    label:
                      <div className="flex items-center gap-2">
                        <img src={productBadge?.badge?.icon?.url} className="max-w-6 max-h-6" />
                        {productBadge?.badge?.display_name}
                      </div>,
                    value: productBadge.id
                  }))}
                />
              </div>
            </div>

            <h2 className="text-base font-bold">Valor e Estoque</h2>

            <div className="flex items-center gap-2">
              <TextField
                label="Valor referência (em reais)"
                value={formatCurrency(Number(initialRewardItem?.reference_price))}
                data-testid="reference_price_input"
                className="w-1/2"
                disabled
              />
              <TextField
                label="Valor para resgate (em pontos)"
                value={String(initialRewardItem?.points_to_redeem)}
                data-testid="points_to_redeem_input"
                className="w-1/2"
                disabled
              />
            </div>

            <div className="flex items-center gap-2">
              <TextField
                label="Data de disponibilidade"
                value={initialRewardItem?.available_from ? formatDate(new Date(initialRewardItem?.available_from)) : undefined}
                data-testid="available_from_input"
                className="w-1/2"
                disabled
              />
              <TextField
                label="Data de expiração"
                value={initialRewardItem?.available_until ? formatDate(new Date(initialRewardItem?.available_until)) : undefined}
                data-testid="available_until_input"
                className="w-1/2"
                disabled
              />
            </div>
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  );
};

export default Show;
